import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["checked", "className", "defaultChecked", "defaultIndeterminate", "disabled", "id", "indeterminate", "inputProps", "intent", "labelText", "labelVariant", "onBlur", "onChange", "onFocus", "name", "size", "spacing", "tabIndex", "value"];

/** @jsx jsx */
import { Text } from '@flowio/react-text';
import { useCombinedRefs, useControlledState } from '@flowio/react-hooks';
import { jsx, useTheme } from '@emotion/react';
import React from 'react';
import Check from './Check';
import Indeterminate from './Indeterminate';
import getStyles from '../utilities/getStyles';
var Checkbox = /*#__PURE__*/React.forwardRef(function (_ref, forwardedRef) {
  var checked = _ref.checked,
      className = _ref.className,
      _ref$defaultChecked = _ref.defaultChecked,
      defaultChecked = _ref$defaultChecked === void 0 ? false : _ref$defaultChecked,
      _ref$defaultIndetermi = _ref.defaultIndeterminate,
      defaultIndeterminate = _ref$defaultIndetermi === void 0 ? false : _ref$defaultIndetermi,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      id = _ref.id,
      indeterminate = _ref.indeterminate,
      inputProps = _ref.inputProps,
      _ref$intent = _ref.intent,
      intent = _ref$intent === void 0 ? 'neutral' : _ref$intent,
      labelText = _ref.labelText,
      _ref$labelVariant = _ref.labelVariant,
      labelVariant = _ref$labelVariant === void 0 ? 'caption' : _ref$labelVariant,
      onBlur = _ref.onBlur,
      onChange = _ref.onChange,
      onFocus = _ref.onFocus,
      name = _ref.name,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'medium' : _ref$size,
      _ref$spacing = _ref.spacing,
      spacing = _ref$spacing === void 0 ? 'md' : _ref$spacing,
      _ref$tabIndex = _ref.tabIndex,
      tabIndex = _ref$tabIndex === void 0 ? disabled ? -1 : 0 : _ref$tabIndex,
      value = _ref.value,
      unhandledProps = _objectWithoutProperties(_ref, _excluded);

  var theme = useTheme();

  var _useControlledState = useControlledState(checked, defaultChecked),
      _useControlledState2 = _slicedToArray(_useControlledState, 2),
      isChecked = _useControlledState2[0],
      setChecked = _useControlledState2[1];

  var _React$useState = React.useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      isFocused = _React$useState2[0],
      setFocused = _React$useState2[1];

  var _useControlledState3 = useControlledState(indeterminate, defaultIndeterminate),
      _useControlledState4 = _slicedToArray(_useControlledState3, 2),
      isIndeterminate = _useControlledState4[0],
      setIndeterminate = _useControlledState4[1];

  var inputRef = React.useRef(null);
  var combinedRef = useCombinedRefs(inputRef, forwardedRef);
  React.useEffect(function () {
    if (inputRef.current != null) {
      inputRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);
  var handleBlur = React.useCallback(function (event) {
    setFocused(false);

    if (typeof onBlur === 'function') {
      onBlur(event);
    }
  }, [onBlur]);
  var handleChange = React.useCallback(function (event) {
    setChecked(!isChecked);
    setIndeterminate(false);

    if (typeof onChange === 'function') {
      onChange(event);
    }
  }, [isChecked, onChange, setChecked, setIndeterminate]);
  var handleFocus = React.useCallback(function (event) {
    setFocused(true);

    if (typeof onFocus === 'function') {
      onFocus(event);
    }
  }, [onFocus]);
  var styles = getStyles({
    checked: isChecked,
    disabled: disabled,
    focused: isFocused,
    indeterminate: isIndeterminate,
    intent: intent,
    size: size,
    spacing: spacing,
    theme: theme
  });
  var checkedIndicator = null;

  if (isIndeterminate) {
    checkedIndicator = jsx(Indeterminate, {
      css: styles.icon
    });
  } else if (isChecked) {
    checkedIndicator = jsx(Check, {
      css: styles.icon
    });
  }

  var textIntent = intent === 'neutral' ? 'textPrimary' : intent;
  return jsx("label", _extends({}, unhandledProps, {
    className: className,
    css: styles.container,
    htmlFor: id
  }), jsx("span", {
    css: styles.checkbox
  }, jsx("input", _extends({}, inputProps, {
    css: styles.input,
    ref: combinedRef,
    type: "checkbox",
    id: id,
    checked: isChecked,
    disabled: disabled,
    name: name,
    onBlur: handleBlur,
    onChange: handleChange,
    onFocus: handleFocus,
    tabIndex: tabIndex,
    value: value
  })), checkedIndicator), labelText != null && jsx(Text, {
    intent: textIntent,
    css: styles.labelText,
    variant: labelVariant
  }, labelText));
});
Checkbox.displayName = 'Checkbox';
export default Checkbox;